import { getEnvVar } from "../Config/AppConfig";

const host = getEnvVar("REACT_APP_AUDIO_SDK_HOST", "localhost");
const port = getEnvVar("REACT_APP_AUDIO_SDK_PORT", "5003");

const enableAudioStream = (deviceId: number | null) => {
  if (deviceId !== null) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch(`http://${host}:${port}/i2sTopic${deviceId}/open_mic`, {
      method: "GET",
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId);
        if (response.ok) {
          console.log(`🎙️ Host PC mic opened to device ${deviceId}`);
        } else {
          console.warn(`⚠️ Open mic returned status ${deviceId}: ${response.status}`);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.warn(`⏱️ Timeout open_mic per device ${deviceId} (5s)`);
        } else {
          console.error("❌ Failed to open host mic:", err);
        }
      });
  }
};

const disableAudioStream = (deviceId: number | null) => {
  if (deviceId !== null) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch(`http://${host}:${port}/i2sTopic${deviceId}/close_mic`, {
      method: "GET",
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId);
        if (response.ok) {
          console.log(`🔇 Host PC mic closed to device ${deviceId}`);
        } else {
          console.warn(`⚠️ Close mic returned status ${deviceId}: ${response.status}`);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.warn(`⏱️ Timeout close_mic per device ${deviceId} (5s)`);
        } else {
          console.error("❌ Failed to close host mic:", err);
        }
      });
  }
};

const openDeviceMic = (deviceId: number | null) => {
  if (deviceId !== null) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch(`http://${host}:${port}/i2sTopic${deviceId}/open_mic_sentinella`, {
      method: "GET",
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId);
        if (response.ok) {
          console.log(`🎤 Device mic opened (sentinella) on ${deviceId}`);
        } else {
          console.warn(`⚠️ Open device mic returned status ${deviceId}: ${response.status}`);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.warn(`⏱️ Timeout open_mic_sentinella per device ${deviceId} (5s)`);
        } else {
          console.error("❌ Failed to open device mic:", err);
        }
      });
  }
};

const closeDeviceMic = (deviceId: number | null) => {
  if (deviceId !== null) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch(`http://${host}:${port}/i2sTopic${deviceId}/close_mic_sentinella`, {
      method: "GET",
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId);
        if (response.ok) {
          console.log(`🔕 Device mic closed (sentinella) on ${deviceId}`);
        } else {
          console.warn(`⚠️ Close device mic returned status ${deviceId}: ${response.status}`);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.warn(`⏱️ Timeout close_mic_sentinella per device ${deviceId} (5s)`);
        } else {
          console.error("❌ Failed to close device mic:", err);
        }
      });
  }
};

// ===== Retry Logic con Exponential Backoff =====

const fetchWithRetry = async (
  url: string,
  maxRetries: number = 3,
  baseDelay: number = 100
): Promise<Response> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(url, {
        method: "GET",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        return response;
      }

      // Se non è l'ultimo tentativo, attendi con exponential backoff
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`⏳ Retry ${attempt + 1}/${maxRetries} tra ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error: any) {
      if (attempt === maxRetries - 1) {
        throw error;
      }
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`⏳ Errore, retry ${attempt + 1}/${maxRetries} tra ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw new Error(`Failed after ${maxRetries} attempts`);
};

// ===== Versioni Async (Attendono risposta HTTP con Retry) =====

const enableAudioStreamAsync = async (deviceId: number | null): Promise<boolean> => {
  if (deviceId !== null) {
    try {
      await fetchWithRetry(
        `http://${host}:${port}/i2sTopic${deviceId}/open_mic`,
        1  // CRITICO: Nessun retry per evitare amplificazione richieste
      );
      console.log(`🎙️ Host PC mic opened to device ${deviceId}`);
      return true;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.warn(`⏱️ Timeout open_mic per device ${deviceId} (5s)`);
      } else {
        console.error("❌ Failed to open host mic after retries:", err);
      }
      return false;
    }
  }
  return false;
};

const disableAudioStreamAsync = async (deviceId: number | null): Promise<boolean> => {
  if (deviceId !== null) {
    try {
      await fetchWithRetry(
        `http://${host}:${port}/i2sTopic${deviceId}/close_mic`,
        1  // CRITICO: Nessun retry per evitare amplificazione richieste
      );
      console.log(`🔇 Host PC mic closed to device ${deviceId}`);
      return true;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.warn(`⏱️ Timeout close_mic per device ${deviceId} (5s)`);
      } else {
        console.error("❌ Failed to close host mic after retries:", err);
      }
      return false;
    }
  }
  return false;
};

const openDeviceMicAsync = async (deviceId: number | null): Promise<boolean> => {
  if (deviceId !== null) {
    try {
      await fetchWithRetry(
        `http://${host}:${port}/i2sTopic${deviceId}/open_mic_sentinella`,
        1  // CRITICO: Nessun retry per evitare amplificazione richieste
      );
      console.log(`🎤 Device mic opened (sentinella) on ${deviceId}`);
      return true;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.warn(`⏱️ Timeout open_mic_sentinella per device ${deviceId} (5s)`);
      } else {
        console.error("❌ Failed to open device mic after retries:", err);
      }
      return false;
    }
  }
  return false;
};

const closeDeviceMicAsync = async (deviceId: number | null): Promise<boolean> => {
  if (deviceId !== null) {
    try {
      await fetchWithRetry(
        `http://${host}:${port}/i2sTopic${deviceId}/close_mic_sentinella`,
        1  // CRITICO: Nessun retry per evitare amplificazione richieste
      );
      console.log(`🔕 Device mic closed (sentinella) on ${deviceId}`);
      return true;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.warn(`⏱️ Timeout close_mic_sentinella per device ${deviceId} (5s)`);
      } else {
        console.error("❌ Failed to close device mic after retries:", err);
      }
      return false;
    }
  }
  return false;
};

const audioControlUtil = {
  // Fire-and-forget (legacy, per retrocompatibilità)
  enableAudioStream,
  disableAudioStream,
  openDeviceMic,
  closeDeviceMic,
  // Async (attendono risposta HTTP)
  enableAudioStreamAsync,
  disableAudioStreamAsync,
  openDeviceMicAsync,
  closeDeviceMicAsync,
};

export default audioControlUtil;
